import Col from "antd/es/col";
import Row from "antd/es/row";
import AntForm from "antd/es/form";
import { PasswordSection } from "features/auth/PasswordSection";
import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import Text from "common/Typography/Text";
import { displayResultMessage, openMessage } from "utils/misc";
import Flex from "common/Boxes/Flex";
import {
  MOBILE_SEARCH,
  REGISTER_PATH,
  REGISTER_VERIFY_EMAIL_PATH,
} from "common/constants";
import React, { useEffect, useState } from "react";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  matchPath,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { RegisterRequest } from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import styled from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import useMedia from "utils/hooks/useMedia";
import { PrimaryTitle } from "./styles";
import { EMAIL_FORM_ITEM_RULES } from "features/auth/constants";
import AuthFormFooter from "features/auth/AuthFormFooter";
import { FixMeLater } from "types";
import useRegisterForm from "@shared/features/onboarding/hooks/useRegisterForm";
import {
  EMAIL_UUID_QUERY_PARAM,
  PRIVACY_POLICY_LINK,
  REGISTERED_EMAIL,
  TERMS_OF_USE_LINK,
} from "@shared/common/constants";
import useQuery from "utils/hooks/useQuery";
import TelegramUserPreview from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/TelegramUserPreview";
import SocialLogins from "features/auth/SocialLogins";
import { sharedLib } from "@shared/services/shared.service";
import Link from "common/Typography/Link.tsx";
import { typography } from "@shared/common/Themes/typography.ts";

const RegisterForm = () => {
  const history = useHistory();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { getUpdatedPath } = useHistoryWithQuery();
  const [form] = AntForm.useForm();
  const location = useLocation();
  const query = useQuery();
  const queryParams = Object.fromEntries(query);
  const isVerifyEmail = !!matchPath(location.pathname, {
    path: REGISTER_VERIFY_EMAIL_PATH,
    exact: true,
    strict: true,
  });
  const referralCode = useRouteMatch<{ referralCode: string }>(REGISTER_PATH)
    ?.params.referralCode;
  const [captchaInProgress, setCaptchaInProgress] = useState(false);
  const [authCode, setAuthCode] = useState<string>();

  const {
    onFinish,
    referralDiscount,
    isLoading,
    checkReferral,
    telegramUserData,
    removeUuid,
  } = useRegisterForm({
    displayResultMessage,
    isVerifyEmail,
    referralCode,
    queryParams,
    onSuccess: (email, emailValidatorUUID) => {
      sessionStorage.setItem(REGISTERED_EMAIL, email);
      if (emailValidatorUUID) {
        history.push(
          getUpdatedPath(
            { [EMAIL_UUID_QUERY_PARAM]: emailValidatorUUID },
            undefined,
            REGISTER_VERIFY_EMAIL_PATH
          )
        );
      }
    },
  });

  const {
    data: captchaData,
    isFetching: isFetchingGetCaptcha,
    isError: getCaptchaError,
    refetch: refetchGetCaptcha,
  } = sharedLib.sharedApi.authentication.useGetCaptchaQuery();
  const [
    validateCaptcha,
    { isFetching: isLoadingValidateCaptcha, data: validateCaptchaData },
  ] = sharedLib.sharedApi.authentication.useLazyValidateCaptchaQuery();
  const [captchaObj, setCaptchaObj] = useState<FixMeLater>();
  const [numCaptchaRefetch, setNumCaptchaRefetch] = useState(0);

  const captchaErrorMessage =
    "Something went wrong when validating captcha information, please try again";
  const maxRefetchErrorMessage =
    "Something went wrong when fetching the captcha information. Please refresh the page and try again";

  const onRefetchCaptcha = () => {
    setCaptchaObj(undefined);
    setNumCaptchaRefetch(numCaptchaRefetch + 1);
    if (numCaptchaRefetch === MAX_NUM_CAPTCHA_REFETCH) {
      openMessage(maxRefetchErrorMessage, "error");
    } else {
      refetchGetCaptcha();
    }
  };

  const onError = (message?: string) => {
    if (message) {
      openMessage(captchaErrorMessage, "error");
    }
    setCaptchaInProgress(false);
    onRefetchCaptcha();
  };

  useEffect(() => {
    if (!isFetchingGetCaptcha && captchaData) {
      (window as FixMeLater).initGeetest(
        {
          gt: captchaData.gt,
          challenge: captchaData.challenge,
          offline: !captchaData.success,
          product: "bind",
          new_captcha: true,
          https: true,
          lang: "en",
        },
        (_captchaObj: FixMeLater) => {
          _captchaObj.onReady(() => {
            setCaptchaObj(_captchaObj);
          });
          _captchaObj.onSuccess(() => {
            const result = _captchaObj.getValidate();
            if (result) {
              validateCaptcha({
                challenge: result.geetest_challenge,
                validate: result.geetest_validate,
                seccode: result.geetest_seccode,
              });
            } else {
              onError(captchaErrorMessage);
            }
          });
          _captchaObj.onClose(function () {
            setCaptchaInProgress(false);
          });
          _captchaObj.onError(() => {
            onError(undefined);
          });
        }
      );
    }
  }, [captchaData, isFetchingGetCaptcha]);

  useEffect(() => {
    if (captchaInProgress && captchaObj) {
      captchaObj.verify();
    }
  }, [captchaInProgress, captchaObj]);

  useEffect(() => {
    if (validateCaptchaData) {
      if (validateCaptchaData?.success && validateCaptchaData?.captcha_token) {
        const registerRequest: RegisterRequest = {
          ...form.getFieldsValue(),
          captcha_token: validateCaptchaData.captcha_token,
          auth_code: authCode,
        };
        onFinish(registerRequest);
        setCaptchaInProgress(false);
      } else {
        onError(captchaErrorMessage);
      }
    }
  }, [validateCaptchaData]);

  const referralCodeMessage = !!referralDiscount && (
    <ReferralCodeMessageWrapper $isMobile={isMobile}>
      <BoldText type="success">You’ve got a referral code!</BoldText>
      <Text type="success">
        You’ll enjoy a {toFixed(referralDiscount * 100, 2)}% discount on annual
        subscriptions.
      </Text>
    </ReferralCodeMessageWrapper>
  );

  const _isLoading =
    isLoading ||
    isLoadingValidateCaptcha ||
    (captchaInProgress && isFetchingGetCaptcha);

  const telegramUserPreview = telegramUserData && (
    <TelegramUserPreview
      horizontal={true}
      showTelegramIcon={true}
      showRemoveUuidButton={true}
      showInfoButton={true}
      telegramUser={telegramUserData}
      style={{ marginBottom: 16 }}
      removeUuid={removeUuid}
    />
  );

  const onSubmitClick = (authCode?: string) => {
    if (authCode) setAuthCode(authCode);
    if (getCaptchaError) {
      onRefetchCaptcha();
    }
    if (!captchaInProgress) {
      setCaptchaInProgress(true);
    }
  };

  return (
    <StyledForm
      name="register"
      form={form}
      initialValues={{
        remember: true,
      }}
      onFinish={() => onSubmitClick()}
      layout="vertical"
      requiredMark={false}
    >
      <PrimaryTitle $isMobile={isMobile} style={{ marginBottom: 24 }}>
        Create Your Account
      </PrimaryTitle>
      {referralCodeMessage}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[
              {
                required: true,
                message: "Please input your first name",
              },
            ]}
          >
            <Input size="large" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[
              {
                required: true,
                message: "Please input your last name",
              },
            ]}
          >
            <Input size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="email" label="Email" rules={EMAIL_FORM_ITEM_RULES}>
        <Input size="large" />
      </Form.Item>
      <PasswordSection size="large" showPlaceholder={false} />
      {checkReferral && (
        <Form.Item name="referral" label="Referral Code">
          <Input placeholder={referralCode} size="large" disabled={true} />
        </Form.Item>
      )}
      {telegramUserPreview}
      <Text $size="S" style={{ marginTop: 20 }}>
        By creating an account, I agree to Cornix's{" "}
        <Link
          href={TERMS_OF_USE_LINK}
          target="blank"
          $size={typography.fontSizes.S}
        >
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link
          href={PRIVACY_POLICY_LINK}
          target="blank"
          $size={typography.fontSizes.S}
        >
          Privacy Policy
        </Link>
      </Text>
      <AuthFormFooter
        buttonLoading={_isLoading}
        buttonStyle={{ marginTop: 20 }}
        isRegister={true}
      />
      <SocialLogins isRegister={true} />
    </StyledForm>
  );
};

export default RegisterForm;

const MAX_NUM_CAPTCHA_REFETCH = 3;

const ReferralCodeMessageWrapper = styled(Flex)<{ $isMobile: boolean }>`
  flex-direction: column;
  background: ${(p) => hexToRGB(p.theme.colors.lightGreen, 0.2)};
  padding: ${(p) => (p.$isMobile ? "8px 4px" : "8px 16px")};
  margin-bottom: 24px;
  text-align: center;
`;
const BoldText = styled(Text)`
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
`;
const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 16px;
  }
`;
